import { useEffect, useSyncExternalStore } from "react";

type ReloadGuard = {
  id: string;
  active: boolean;
  busy: boolean;
  dirty: boolean;
  label?: string;
};

type UpdateSnapshot = {
  pending: boolean;
  pendingSince: number | null;
  blocked: boolean;
  blockingLabels: string[];
  showPrompt: boolean;
  lastCheckpoint: string | null;
};

declare global {
  interface Window {
    __sloeSwUpdate?: boolean;
    __sloeSwUpdateAt?: number;
  }
}

const IDLE_MS = 60_000;
const PROMPT_RESURFACE_MS = 90_000;
const guards = new Map<string, ReloadGuard>();
const listeners = new Set<() => void>();

let pendingSince: number | null = null;
let promptVisible = false;
let snoozedUntil = 0;
let reloading = false;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let promptTimer: ReturnType<typeof setTimeout> | null = null;
let lastActivityAt = Date.now();
let lastCheckpoint: string | null = null;
let runtimeInitialized = false;

let cachedSnapshot: UpdateSnapshot = {
  pending: false,
  pendingSince: null,
  blocked: false,
  blockingLabels: [],
  showPrompt: false,
  lastCheckpoint: null,
};

function sameStrings(a: string[], b: string[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function rebuildSnapshot() {
  const blocked = blockingGuards();
  const nextLabelsRaw = blocked.map((guard) => guard.label || guard.id);
  const nextLabels = sameStrings(cachedSnapshot.blockingLabels, nextLabelsRaw)
    ? cachedSnapshot.blockingLabels
    : nextLabelsRaw;
  const nextSnapshot: UpdateSnapshot = {
    pending: Boolean(pendingSince),
    pendingSince,
    blocked: blocked.length > 0,
    blockingLabels: nextLabels,
    showPrompt: Boolean(pendingSince) && promptVisible,
    lastCheckpoint,
  };
  const unchanged =
    cachedSnapshot.pending === nextSnapshot.pending &&
    cachedSnapshot.pendingSince === nextSnapshot.pendingSince &&
    cachedSnapshot.blocked === nextSnapshot.blocked &&
    cachedSnapshot.blockingLabels === nextSnapshot.blockingLabels &&
    cachedSnapshot.showPrompt === nextSnapshot.showPrompt &&
    cachedSnapshot.lastCheckpoint === nextSnapshot.lastCheckpoint;
  if (!unchanged) cachedSnapshot = nextSnapshot;
  return cachedSnapshot;
}

function emit() {
  const previous = cachedSnapshot;
  const next = rebuildSnapshot();
  if (previous === next) return;
  for (const listener of listeners) listener();
}

function blockingGuards(): ReloadGuard[] {
  return Array.from(guards.values()).filter((guard) => guard.active && (guard.busy || guard.dirty));
}

function updatePromptVisibility() {
  if (!pendingSince) {
    promptVisible = false;
    return;
  }
  const blocked = blockingGuards().length > 0;
  promptVisible = blocked && Date.now() >= snoozedUntil;
}

function getSnapshot(): UpdateSnapshot {
  return cachedSnapshot;
}

function clearPromptTimer() {
  if (promptTimer) {
    clearTimeout(promptTimer);
    promptTimer = null;
  }
}

function clearIdleTimer() {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
}

function schedulePromptResurface() {
  clearPromptTimer();
  if (!pendingSince || Date.now() >= snoozedUntil) return;
  promptTimer = setTimeout(() => {
    updatePromptVisibility();
    emit();
  }, Math.max(0, snoozedUntil - Date.now()));
}

function attemptReload(reason: string): boolean {
  lastCheckpoint = reason;
  if (!pendingSince || reloading) return false;
  if (blockingGuards().length > 0) {
    updatePromptVisibility();
    emit();
    return false;
  }
  reloading = true;
  try {
    console.info("[sw-update] reloading at checkpoint:", reason);
  } catch {
    // ignore
  }
  window.location.reload();
  return true;
}

function scheduleIdleReload() {
  clearIdleTimer();
  if (!pendingSince) return;
  const wait = Math.max(0, IDLE_MS - (Date.now() - lastActivityAt));
  idleTimer = setTimeout(() => {
    if (!attemptReload("idle")) {
      scheduleIdleReload();
    }
  }, wait);
}

function markPendingUpdate(updatedAt?: number) {
  pendingSince = updatedAt || Date.now();
  promptVisible = blockingGuards().length > 0;
  snoozedUntil = 0;
  scheduleIdleReload();
  emit();
}

function recordActivity() {
  lastActivityAt = Date.now();
  if (pendingSince) scheduleIdleReload();
}

function initRuntime() {
  if (runtimeInitialized || typeof window === "undefined") return;
  runtimeInitialized = true;

  if (window.__sloeSwUpdate) {
    pendingSince = window.__sloeSwUpdateAt || Date.now();
  }
  rebuildSnapshot();

  window.addEventListener("sloe:sw-updated", (event: Event) => {
    const detail = (event as CustomEvent<{ updatedAt?: number }>).detail;
    markPendingUpdate(detail?.updatedAt);
  });

  window.addEventListener("focus", () => {
    notifySafeCheckpoint("focus");
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      notifySafeCheckpoint("visible");
    } else {
      recordActivity();
    }
  });

  const activityEvents: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "mousedown", "touchstart"];
  activityEvents.forEach((type) => window.addEventListener(type, recordActivity, { passive: true }));

  scheduleIdleReload();
}

initRuntime();

export function useReloadGuard(guard: ReloadGuard) {
  useEffect(() => {
    initRuntime();
    guards.set(guard.id, guard);
    updatePromptVisibility();
    emit();
    return () => {
      guards.delete(guard.id);
      updatePromptVisibility();
      emit();
    };
  }, [guard.id, guard.active, guard.busy, guard.dirty, guard.label]);
}

export function notifySafeCheckpoint(reason: string) {
  initRuntime();
  recordActivity();
  attemptReload(reason);
}

export function useUpdateReloadState(): UpdateSnapshot {
  initRuntime();
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot,
    getSnapshot,
  );
}

export function forceReloadNow() {
  initRuntime();
  if (!pendingSince) return;
  lastCheckpoint = "reload-now";
  reloading = true;
  window.location.reload();
}

export function snoozePendingUpdate() {
  initRuntime();
  if (!pendingSince) return;
  snoozedUntil = Date.now() + PROMPT_RESURFACE_MS;
  promptVisible = false;
  schedulePromptResurface();
  emit();
}
